import Box from '@material-ui/core/Box';
import Chip from '@material-ui/core/Chip';
import MenuItem from '@material-ui/core/MenuItem';
import Paper from '@material-ui/core/Paper';
import { Theme, createStyles, emphasize, makeStyles, useTheme } from '@material-ui/core/styles';
import TextField, { BaseTextFieldProps } from '@material-ui/core/TextField';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import CancelIcon from '@material-ui/icons/Cancel';
import clsx from 'clsx';
import _truncate from 'lodash.truncate';
import * as React from 'react';
import Select from 'react-select';
import { ValueContainerProps } from 'react-select/src/components/containers';
import { ControlProps } from 'react-select/src/components/Control';
import { MenuProps, NoticeProps } from 'react-select/src/components/Menu';
import { MultiValueProps } from 'react-select/src/components/MultiValue';
import { OptionProps } from 'react-select/src/components/Option';

import { SearchField } from '../models/SearchField';
import OperatorDropdownButton from './OperatorDropdownButton';
import RemoveFieldButton from './RemoveFieldButton';
import TextDropdownButton from './TextDropdownButton';

export interface Props {
  onChangeField: (searchField: Partial<SearchField>) => void;
  onRemoveField: (searchFieldId: string) => void;
  onSelectReplaceField: (newFieldName: Pick<SearchField, 'fieldName' | 'id'>) => void;

  options: string[];
  fieldOptions: string[];
  searchField: any;
}

export interface OptionType {
  label: string;
  value: string;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      minHeight: 48, // TODO. This could be moved to theme. Maybe use toolbar min-height?
      '&:hover $removeButton': {
        display: 'block' // Show remove button on hover, hidden by default
      }
    },
    textButton: {
      flex: '1 1 0%',
      margin: 6, // Hardcode to get exact spacing needed regardless of theme
      alignSelf: 'stretch'
    },
    input: {
      display: 'flex',
      padding: 0,
      height: 'auto'
    },
    // Hardcode label and icon to get as much real estate from the chips as possible
    label: {
      padding: 5,
      overflow: 'hidden',
      marginRight: 2
    },
    icon: {
      marginRight: 1
    },
    valueContainer: {
      display: 'flex',
      flexWrap: 'wrap',
      flex: 1,
      alignItems: 'center',
      overflow: 'hidden'
    },
    chip: {
      margin: theme.spacing(0.125),
      fontSize: theme.typography.caption.fontSize
    },
    chipFocused: {
      backgroundColor: emphasize(
        theme.palette.type === 'light' ? theme.palette.grey[300] : theme.palette.grey[700],
        0.08
      )
    },
    paper: {
      position: 'absolute',
      zIndex: 1,
      marginTop: theme.spacing(1),
      left: -80,
      right: -20
    },
    operator: {
      flex: '0 0 auto'
    },
    removeButton: {
      display: 'none',
      position: 'absolute',
      right: -14
    }
  })
);

function NoOptionsMessage(props: NoticeProps<OptionType>) {
  return (
    <Typography color="textSecondary" {...props.innerProps}>
      {props.children}
    </Typography>
  );
}

type InputComponentProps = Pick<BaseTextFieldProps, 'inputRef'> &
  React.HTMLAttributes<HTMLDivElement>;

function inputComponent({ inputRef, ...props }: InputComponentProps) {
  return <div ref={inputRef} {...props} />;
}

function Control(props: ControlProps<OptionType>) {
  const {
    children,
    innerProps,
    innerRef,
    selectProps: { classes, TextFieldProps }
  } = props;

  return (
    <TextField
      fullWidth={true}
      InputProps={{
        inputComponent,
        inputProps: {
          className: classes.input,
          ref: innerRef,
          children,
          ...innerProps
        }
      }}
      {...TextFieldProps}
    />
  );
}

function Option(props: OptionProps<OptionType>) {
  return (
    <MenuItem
      ref={props.innerRef}
      selected={props.isFocused}
      component="div"
      style={{
        fontWeight: props.isSelected ? 500 : 400
      }}
      {...props.innerProps}
    >
      {props.children}
    </MenuItem>
  );
}

function ValueContainer(props: ValueContainerProps<OptionType>) {
  return <div className={props.selectProps.classes.valueContainer}>{props.children}</div>;
}

function MultiValue(props: MultiValueProps<OptionType>) {
  return (
    <Tooltip title={props.children} enterDelay={400}>
      <Chip
        tabIndex={-1}
        label={_truncate(props.children, { length: 14 })}
        classes={{
          label: props.selectProps.classes.label,
          deleteIcon: props.selectProps.classes.icon
        }}
        className={clsx(props.selectProps.classes.chip, {
          [props.selectProps.classes.chipFocused]: props.isFocused
        })}
        onDelete={props.removeProps.onClick}
        deleteIcon={<CancelIcon {...props.removeProps} />}
      />
    </Tooltip>
  );
}

function Menu(props: MenuProps<OptionType>) {
  return (
    <Paper square={true} className={props.selectProps.classes.paper} {...props.innerProps}>
      {props.children}
    </Paper>
  );
}

const components = {
  Control,
  Menu,
  MultiValue,
  NoOptionsMessage,
  Option,
  ValueContainer
};

const DropdownSearchField: React.FC<Props> = ({
  options,
  onChangeField,
  onRemoveField,
  onSelectReplaceField,
  fieldOptions,
  searchField
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const inputRef: React.MutableRefObject<any> = React.useRef();

  React.useEffect(() => {
    if (!searchField || !inputRef || !inputRef.current) {
      return;
    }
    const timeout = setTimeout(() => {
      inputRef.current.focus();
    }, 100);

    return () => {
      clearTimeout(timeout);
    };
  }, [searchField]);

  const suggestedOptions: OptionType[] = options.map(option => ({
    value: option,
    label: option
  }));

  const currentSelectedOptions: OptionType[] = searchField.value.map(selectedOption => ({
    value: selectedOption,
    label: selectedOption
  }));

  const selectStyles = {
    container: style => ({
      ...style,
      flex: '1.5 1 0%',
      marginTop: 6,
      marginBottom: 6,
      marginLeft: 12,
      marginRight: 14
    }),
    input: style => ({
      ...style,
      color: theme.palette.text.primary,
      '& input': {
        font: 'inherit'
      }
    }),
    placeholder: style => ({ ...style, fontSize: theme.typography.body2.fontSize }),
    indicatorsContainer: style => ({
      ...style,
      display: 'none'
    })
  };

  const onChange = (key, changedValue) => {
    if (key === 'fieldName') {
      onSelectReplaceField({
        id: searchField.id,
        fieldName: changedValue
      });
      return;
    }
    onChangeField({
      id: searchField.id,
      value: Array.isArray(changedValue) ? changedValue.map(value => value.value) : []
    });
  };

  return (
    <Box
      className={classes.root}
      component="section"
      position="relative"
      display="flex"
      flexDirection="row"
      alignItems="center"
      bgcolor="background.default"
    >
      <TextDropdownButton
        className={classes.textButton}
        currentOption={searchField.fieldName}
        availableOptions={fieldOptions}
        onSelect={onChange.bind(null, 'fieldName')}
      />
      <OperatorDropdownButton
        className={classes.operator}
        currentOption="="
        availableOptions={['=']}
        disabled={true}
        onSelect={onChange.bind(null, 'operator')}
      />
      <Select
        classes={classes}
        ref={inputRef}
        styles={selectStyles}
        inputId="react-select-multiple"
        placeholder={searchField.fieldName}
        options={suggestedOptions}
        components={components}
        value={currentSelectedOptions}
        onChange={onChange.bind(null, 'value')}
        isMulti={true}
      />
      <RemoveFieldButton
        className={classes.removeButton}
        onClick={onRemoveField}
        searchFieldId={searchField.id}
      />
    </Box>
  );
};

export default DropdownSearchField;
